/* eslint-disable no-unused-vars */
import {
  useCreateAchOrderV2,
  type CreateAchOrderErrorType,
  type CreateAchOrderInputV2,
  type CreateAchOrderOutput,
} from '@egw/api'
import { type GQLError } from '@egw/clients'
import { DEFAULT_CURRENCY_CODE } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { Currency } from '@egw/utils'
import { useCallback } from 'react'
import { type UseFormSetError } from 'react-hook-form'
import { AMOUNT_KEY } from '../constants'
import {
  TransferSuccessTypes,
  type BackendError,
  type EBackendError,
  type IGQLErrorResponse,
  type TransferFormInterface,
} from '../types'
import { handleGenericBackendErrors } from '../utils'

interface UseAchTransferInterface {
  onSuccess: (successType: TransferSuccessTypes) => void
  onError: (errorType: CreateAchOrderErrorType | EBackendError | 'BankingMinimumDepositNotMetError') => void
  onInlineError: UseFormSetError<TransferFormInterface>
}

export const useAchTransfer = ({ onSuccess, onError, onInlineError }: UseAchTransferInterface) => {
  const { t } = useTranslation('features-transfer')

  const onAchTransferSuccess = useCallback(
    (data: CreateAchOrderOutput, variables?: CreateAchOrderInputV2) => {
      const createAchOrderV2 = data

      const achOrderTypename = createAchOrderV2.__typename
      switch (achOrderTypename) {
        case 'CreateAchOrderError':
          onError(createAchOrderV2.type)
          break

        case 'CreateAchOrderLimitError':
          onInlineError(AMOUNT_KEY, {
            message: t(
              createAchOrderV2.remaining === 0
                ? `transfer.error.${createAchOrderV2.limitType}_DEPLETED`
                : `transfer.error.${createAchOrderV2.limitType}`,
              {
                limit: t(`currency.${DEFAULT_CURRENCY_CODE}`, {
                  amount: Currency.integerCents.omitSign(Number(createAchOrderV2.limit)),
                }),
                remaining: t(`currency.${DEFAULT_CURRENCY_CODE}`, {
                  amount: Currency.integerCents.omitSign(Number(createAchOrderV2.remaining)),
                }),
              },
            ),
          })
          break

        case 'BankingMinimumDepositNotMetError': {
          const amount =
            data.__typename === 'BankingMinimumDepositNotMetError' ? data.minimumDepositRequirementAmount : 0

          onInlineError(AMOUNT_KEY, {
            // WEALTH APPENDED TO DIFFERENTIATE MESSAGES BETWEEN WEALTH AND MONEY APP 10k vs 25k Min Deposit
            message: t(`transfer.error.BankingMinimumDepositNotMetError`, {
              amount: Currency.integerCents.omitSign(amount),
            }),
          })

          break
        }

        case 'CreateAchOrderSuccess':
        default:
          onSuccess(TransferSuccessTypes.Ach)
          break
      }
    },
    [onInlineError, onSuccess, onError, t],
  )

  const onAchTransferError = useCallback(
    (backendError: GQLError | IGQLErrorResponse | BackendError[]) => {
      const errorType = handleGenericBackendErrors({
        backendError,
      })
      onError(errorType)
    },
    [onError],
  )

  const { mutate: createAchOrderMutation, isLoading: achOrderIsLoading } = useCreateAchOrderV2({
    onSuccess: onAchTransferSuccess,
    onError: onAchTransferError,
  })

  return {
    createAchOrderMutation,
    achOrderIsLoading,
  }
}
